/**
 * Native Geräte-Bridge der Android-App „Bilanx Kassa“ (SUNMI T3): `window.BilanxGeraet`.
 *
 * Die App legt das Objekt nur auf der bestätigten Kassen-Origin und nur im Hauptframe an
 * (Vertrag von A1, `docs/android/sunmi-t3-hardware.md`). Fehlt es – Browser, PWA, Windows –,
 * bleibt alles beim heutigen Weg über Server und Hardware-Bridge.
 *
 * Alle Methoden sind asynchron. Erfolg: Objekt mit Daten (`wiederholt: true`, wenn derselbe
 * Idempotenzschlüssel schon erledigt war – dann wurde NICHT erneut gedruckt). Fehler: Error
 * mit `code` und `unklar` (true = Ausgang unbekannt, nie automatisch wiederholen).
 */

export interface GeraetFaehigkeiten {
  drucker: boolean;
  papierbreiteMm: number | null;
  zeichenProZeile: number | null;
  lade: boolean;
  kundendisplay: boolean;
  kundendisplayZeilen: number | null;
  hersteller: string;
  modell: string;
  /** false, solange die Fähigkeit nicht an einer echten SUNMI T3 geprüft ist. */
  geprueftAmGeraet: boolean;
}

export interface GeraetErgebnis {
  /** Gerät hat die Ausführung bestätigt (SUNMI: Rückmeldung nach dem Druck). */
  bestaetigt?: boolean;
  /** Gleicher Schlüssel schon erledigt: gespeichertes Ergebnis, nichts erneut ausgeführt. */
  wiederholt?: boolean;
  /** Zusatzhinweis des Geräts, z. B. „Papier fast leer“. */
  hinweis?: string;
  [feld: string]: unknown;
}

/** Bonmodell wie `details` eines Druckauftrags der API bzw. `Bon` der Hardware-Bridge. */
export interface Bonmodell {
  format: 'bilanx-bon/1';
  titel?: string;
  zeilen: string[];
  qrCode?: string;
  kopie?: boolean;
}

export interface BilanxGeraet {
  readonly version: 1;
  faehigkeiten(): Promise<GeraetFaehigkeiten>;
  drucke(beleg: string | Bonmodell, idempotenzSchluessel: string): Promise<GeraetErgebnis>;
  oeffneLade(idempotenzSchluessel: string): Promise<GeraetErgebnis>;
  kundendisplay(zeilen: string[]): Promise<GeraetErgebnis>;
}

/** Fehler der Bridge, auf eine feste Form gebracht. */
export interface GeraetFehler {
  code: string;
  text: string;
  unklar: boolean;
  /**
   * Gleicher Idempotenzschlüssel war schon verbraucht: Die Bridge liefert das gespeicherte frühere
   * Ergebnis, jetzt wurde nichts ausgeführt. Ein neuer Versuch braucht einen neuen Schlüssel.
   */
  wiederholt: boolean;
}

/**
 * Codes, mit denen die Bridge „sicher nicht ausgeführt“ meldet – das Gerät wurde für DIESEN Aufruf
 * nicht angesprochen. Abgeglichen mit A1 (`sunmi1-app` 901a4bd `BridgeProtokoll.SICHER_NICHT_BEGONNEN`, `BridgeVerteiler.kt`,
 * `Schnittstellen.kt` `Reservierung.Abgelehnt`, `GeraeteBridge.kt`), A2 (Hardwaredienst,
 * Tabelle in `docs/android/sunmi-t3-hardware.md` Abschnitt 3) und A3 (`nicht_erlaubt`).
 * Jeder andere Code – auch ein unbekannter oder `unbekannt` aus `bilanx-geraet.js` – gilt als
 * unklar (Codex Runde 3 N7, Runde 3b MITTEL 4).
 */
export const SICHER_NICHT_AUSGEFUEHRT: ReadonlySet<string> = new Set([
  // A1
  'abgelehnt',
  'schluessel_anderer_inhalt',
  'nicht_begonnen',
  'ueberlastet',
  // A1/A2/A3 Vorprüfung
  'ungueltig',
  'nicht_verfuegbar',
  'nicht_erlaubt',
  // A2 Hardwaredienst
  'keine_nutzeraktion',
  'zu_viele_auftraege',
  'kein_papier',
  'papierstau',
  'falsches_papier',
  'deckel_offen',
  'ueberhitzt',
  'messerfehler',
  'drucker_nicht_bereit',
]);

/**
 * Gleicher Schlüssel war schon mit anderem Inhalt in Gebrauch: Dieser Aufruf hat nichts gedruckt,
 * der frühere Versuch unter dem Schlüssel aber vielleicht.
 */
export const CODE_ANDERER_INHALT = 'schluessel_anderer_inhalt';

/**
 * Fehler der Bridge auf eine feste Form bringen. „Sicher nicht ausgeführt“ (`unklar: false`)
 * gilt nur, wenn die Bridge ausdrücklich `unklar: false` mit einem Code aus
 * [SICHER_NICHT_AUSGEFUEHRT] meldet. Alles andere – fehlender Code, fehlendes Flag, unbekannter
 * Code – gilt als unklar (Codex Runde 2 Fund 11, Runde 3 N7): lieber „Ausdruck prüfen“ als ein
 * stiller Doppeldruck. `wiederholt` wird übernommen, aber nur bei ausdrücklichem `true`.
 */
export function alsGeraetFehler(e: unknown): GeraetFehler {
  if (typeof e === 'object' && e !== null) {
    const o = e as {
      code?: unknown;
      message?: unknown;
      text?: unknown;
      unklar?: unknown;
      wiederholt?: unknown;
    };
    const mitCode = typeof o.code === 'string' && o.code !== '';
    const code = mitCode ? (o.code as string) : 'interner_fehler';
    const text =
      typeof o.message === 'string' && o.message !== ''
        ? o.message
        : typeof o.text === 'string' && o.text !== ''
          ? o.text
          : 'Unbekannter Gerätefehler';
    const sicher = o.unklar === false && SICHER_NICHT_AUSGEFUEHRT.has(code);
    return { code, text, unklar: !sicher, wiederholt: o.wiederholt === true };
  }
  return { code: 'interner_fehler', text: String(e), unklar: true, wiederholt: false };
}

/**
 * Die native Bridge, wenn diese Seite in der Android-App läuft, sonst null. Prüft Form und
 * Version, damit eine fremde oder ältere Variable nicht als Bridge gilt.
 */
export function findeGeraet(
  umgebung: { BilanxGeraet?: unknown } = globalThis as { BilanxGeraet?: unknown },
): BilanxGeraet | null {
  const g = umgebung.BilanxGeraet;
  if (typeof g !== 'object' || g === null) {
    return null;
  }
  const o = g as Record<string, unknown>;
  if (o.version !== 1) {
    return null;
  }
  for (const m of ['faehigkeiten', 'drucke', 'oeffneLade', 'kundendisplay']) {
    if (typeof o[m] !== 'function') {
      return null;
    }
  }
  return g as BilanxGeraet;
}
